import { Column, ColumnDef } from '@tanstack/react-table';
import { createStore } from '@xstate/store';
import { useSelector } from '@xstate/store/react';
import capitalize from 'lodash/capitalize';
import { match } from 'ts-pattern';

import { formatToLocale } from '@eluve/date-utils';
import { OnlyStringKeys } from '@eluve/utils';

import { Button } from '../Button';
import { Icon } from '../Icon';
import { Stack } from '../Stack';
import { Text } from '../Typography';
import { Link } from '../link';
import { cn } from '../utils/cn';

interface SortableHeaderProps<T> {
  column: Column<T>;
  label: React.ReactNode;
}

type ColArgs<T> = {
  label?: string;
  cellRenderer?: (row: T) => React.ReactNode;
  colOptions?: Partial<ColumnDef<T>>;
  isPrivate?: boolean;
};
export const SortableColumnHeader = <T,>(props: SortableHeaderProps<T>) => {
  const { column, label } = props;
  const sorted = column.getIsSorted();

  return (
    <Button
      variant="link"
      className="px-0 whitespace-nowrap text-text-tertiary hover:text-text-secondary"
      onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
    >
      {label}
      {sorted === false && <Icon name="ArrowUpDown" className="ml-2" />}
      {sorted === 'asc' && <Icon name="ArrowUp" className="ml-2" />}
      {sorted === 'desc' && <Icon name="ArrowDown" className="ml-2" />}
    </Button>
  );
};

/**
 * A utility to create a simple sortable column definition
 * which will render the column value as is
 */
export const makeDefaultSortableColumnDef = <T extends Record<string, any>>(
  col: OnlyStringKeys<keyof T>,
  optionsOrLabel?: string | ColArgs<T>,
) => {
  const options =
    typeof optionsOrLabel === 'string'
      ? { label: optionsOrLabel }
      : optionsOrLabel;

  const { label, cellRenderer, colOptions, isPrivate } = options ?? {};
  const colDef: ColumnDef<T> = {
    ...colOptions,
    accessorKey: col,
    sortUndefined: -1,
    header: ({ column }) => (
      <SortableColumnHeader column={column} label={label ?? capitalize(col)} />
    ),
    cell: ({ row }) => {
      if (cellRenderer) {
        return cellRenderer(row.original);
      }

      const val =
        typeof row.original[col] === 'boolean'
          ? `${row.original[col]}`
          : row.original[col];
      if (!val) {
        return <Text color="disabled">—</Text>;
      }
      return <Text className={cn(isPrivate && 'privacy-text')}>{val}</Text>;
    },
  };

  return colDef;
};

/**
 * A utility to create a simple sortable column definition
 * which will render the column value as is
 */
export const makeDefaultSortableLinkDef = <T extends Record<string, any>>(
  col: OnlyStringKeys<keyof T>,
  linkFn: (row: T) => string,
  label?: string,
) => {
  const colDef: ColumnDef<T> = {
    accessorKey: col,
    header: ({ column }) => (
      <SortableColumnHeader column={column} label={label ?? capitalize(col)} />
    ),
    cell: ({ row }) => {
      return <Link to={linkFn(row.original)}>{row.original[col]}</Link>;
    },
  };

  return colDef;
};

const makeDetailsLinkColumnDef = <T extends Record<string, any>>(
  linkFn: (row: T) => string,
  label = 'Details',
) => {
  const colDef: ColumnDef<T> = {
    header: label,
    cell: ({ row }) => {
      return (
        <Link to={linkFn(row.original)}>
          {/* The icon is aria-hidden, so without this label the button has no
              accessible name at all — screen readers announce a bare "button",
              and tests are forced into positional `td:first > button` chains. */}
          <Button variant="outline" aria-label={label}>
            <Icon name="ArrowBigRight" size="xl" />
          </Button>
        </Link>
      );
    },
  };

  return colDef;
};

/**
 * A utility for creating a sortable column definition for a date column
 * This takes care of consistent formatting of the date as well as sane
 * sorting behavior
 */
export const makeSortableDateColumnDef = <T extends Record<string, any>>(
  col: OnlyStringKeys<keyof T>,
  optionsOrLabel?: string | Omit<ColArgs<T>, 'cellRenderer'>,
) => {
  const options =
    typeof optionsOrLabel === 'string'
      ? { label: optionsOrLabel }
      : optionsOrLabel;

  const { label, colOptions, isPrivate } = options ?? {};

  const colDef: ColumnDef<T> = {
    ...colOptions,
    accessorKey: col,
    accessorFn: (row) => {
      // Convert null to undefined so that we can benefit from the sortUndefined behavior below
      return row[col] ?? undefined;
    },
    sortUndefined: -1,
    header: ({ column }) => (
      <SortableColumnHeader column={column} label={label ?? capitalize(col)} />
    ),
    cell: ({ row }) => {
      const val = row.original[col];
      if (!val) {
        return <Text color="disabled">—</Text>;
      }
      return (
        <Text className={cn(isPrivate && 'privacy-text')}>
          {formatToLocale(row.original[col])}
        </Text>
      );
    },
  };

  return colDef;
};

/**
 * A utility for creating a column definition that has a visibility toggle
 * On click of the toggle button, the column will switch between the
 * visible `cell` renderer and the `hiddenCell` renderer
 * @param colDef Additional column definition properties
 * @returns ColumnDef with visibility toggle functionality on the header
 */
export const makeVisibilityToggleColumnDef = <T,>(
  colDef: ColumnDef<T> & {
    hiddenCell: ColumnDef<T>['cell'];
  },
): ColumnDef<T> => {
  const visibilityStore = createStore(
    { visible: false },
    {
      toggle: (ctx: { visible: boolean }) => ({ visible: !ctx.visible }),
    },
  );
  const {
    hiddenCell: hiddenCellRender,
    cell: visibleCellRender,
    header,
  } = colDef;

  return {
    ...colDef,
    header: (...args) => {
      // eslint-disable-next-line react-hooks/rules-of-hooks
      const visibility = useSelector(visibilityStore, (s) => s.context.visible);
      const headerContent =
        typeof header === 'function' ? header(...args) : header;

      return (
        <Stack direction="horizontal" fullWidth={false} gap={2}>
          <Button
            onClick={() => visibilityStore.send({ type: 'toggle' })}
            variant="link"
            className="text-text-secondary"
          >
            {visibility ? (
              <Icon name="EyeOff" size="lg" />
            ) : (
              <Icon name="Eye" size="lg" />
            )}
          </Button>
          {headerContent}
        </Stack>
      );
    },
    cell: (...args) => {
      // eslint-disable-next-line react-hooks/rules-of-hooks
      const visibility = useSelector(visibilityStore, (s) => s.context.visible);

      if (visibility) {
        return typeof visibleCellRender === 'function'
          ? visibleCellRender(...args)
          : visibleCellRender;
      }

      return typeof hiddenCellRender === 'function'
        ? hiddenCellRender(...args)
        : hiddenCellRender;
    },
  } as ColumnDef<T>;
};

export class ColDefBuilder<T extends Record<string, any>> {
  private readonly colDefs: ColumnDef<T>[] = [];

  /**
   * Adds a default sortable column. This will simply render the column value
   */
  defaultSortable<K extends OnlyStringKeys<keyof T>>(
    col: K,
    label?: string,
  ): ColDefBuilder<T>;

  defaultSortable<K extends OnlyStringKeys<keyof T>>(
    col: K,
    options?: ColArgs<T>,
  ): ColDefBuilder<T>;

  defaultSortable<K extends OnlyStringKeys<keyof T>>(
    col: K,
    labelOrOptions?: string | ColArgs<T>,
  ) {
    if (typeof labelOrOptions === 'string') {
      this.colDefs.push(
        makeDefaultSortableColumnDef(col, { label: labelOrOptions }),
      );
    } else {
      this.colDefs.push(makeDefaultSortableColumnDef(col, labelOrOptions));
    }

    return this;
  }

  /**
   * Adds a default sortable column. This will simply render the column value
   */
  defaultBoolean<K extends OnlyStringKeys<keyof T>>(col: K, label?: string) {
    this.colDefs.push(
      makeDefaultSortableColumnDef(col, {
        label,
        cellRenderer: (row) =>
          match(row[col] as boolean)
            .with(true, () => (
              <Icon
                name="Check"
                className="text-text-primary"
                size="lg"
                aria-label="Yes"
              />
            ))
            .with(false, () => (
              <Icon
                name="X"
                className="text-text-disabled"
                size="lg"
                aria-label="No"
              />
            ))
            .otherwise(() => <Text color="disabled">—</Text>),
      }),
    );
    return this;
  }

  /**
   * Adds a sortable column that renders a link.
   */
  linkSortable<K extends OnlyStringKeys<keyof T>>(
    col: K,
    linkFn: (row: T) => string,
    label?: string,
  ) {
    this.colDefs.push(makeDefaultSortableLinkDef(col, linkFn, label));
    return this;
  }

  detailsLink(linkFn: (row: T) => string, label?: string) {
    this.colDefs.push(makeDetailsLinkColumnDef(linkFn, label));
    return this;
  }

  /**
   * Adds a sortable date column which will format the date automatically
   * and use sane sorting behavior for undefined dates
   */

  dateSortable<K extends OnlyStringKeys<keyof T>>(
    col: K,
    labelOrOptions?: string | Omit<ColArgs<T>, 'cellRenderer'>,
  ) {
    const options =
      typeof labelOrOptions === 'string'
        ? { label: labelOrOptions }
        : labelOrOptions;

    this.colDefs.push(makeSortableDateColumnDef(col, options));
    return this;
  }

  /**
   * Adds a custom column definition
   */
  colDef(colDef: ColumnDef<T>) {
    this.colDefs.push(colDef);
    return this;
  }

  /**
   * Returns the column definitions to be used with a DataGrid
   */
  build() {
    return this.colDefs;
  }
}
